import { useState } from "react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { Check, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface EmailFieldProps {
  form: any;
}

const EmailField = ({ form }: EmailFieldProps) => {
  const { toast } = useToast();
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState("");
  const [emailError, setEmailError] = useState("");
  const emailValue = form.watch("email");
  const isVerified = form.watch("emailVerified");
  const [isSending, setIsSending] = useState(false);
  const [sentCode, setSentCode] = useState(false);

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError("");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Enter a valid email address");
      return false;
    }

    const emailDomain = email.split("@")[1]?.toLowerCase();
    if (!emailDomain) {
      setEmailError("Email must include a domain");
      return false;
    }

    // Validate TLD (top-level domain) has at least 2 characters
    const tld = emailDomain.split(".").pop();
    if (!tld || tld.length < 2) {
      setEmailError("Please enter a valid email domain");
      return false;
    }

    // Block disposable email domains
    const disposableDomains = [
      "10minutemail.com", "guerrillamail.com", "tempmail.com", "throwaway.email",
      "mailinator.com", "maildrop.cc", "temp-mail.org", "yopmail.com",
      "getnada.com", "fakeinbox.com", "trashmail.com", "discard.email",
      "sharklasers.com", "grr.la", "spam4.me", "tempr.email"
    ];

    if (disposableDomains.includes(emailDomain)) {
      setEmailError("Disposable email addresses are not allowed");
      return false;
    }

    // Validate common email providers for correct spelling
    const commonProviders: { [key: string]: string } = {
      "gmail.com": "gmail.com",
      "yahoo.com": "yahoo.com",
      "icloud.com": "icloud.com",
      "outlook.com": "outlook.com",
      "hotmail.com": "hotmail.com",
      "aol.com": "aol.com",
    };

    // Check for common misspellings
    const misspellings: { [key: string]: string } = {
      "gmail.co": "gmail.com",
      "gmial.com": "gmail.com",
      "gmai.com": "gmail.com",
      "yahoo.co": "yahoo.com",
      "yahooo.com": "yahoo.com",
      "yaho.com": "yahoo.com",
      "icloud.co": "icloud.com",
      "icould.com": "icloud.com",
      "iclod.com": "icloud.com",
      "outlook.co": "outlook.com",
      "outlok.com": "outlook.com",
      "hotmail.co": "hotmail.com",
      "hotmai.com": "hotmail.com",
      "aol.co": "aol.com",
      "google.co": "gmail.com",
    };

    if (misspellings[emailDomain]) {
      setEmailError(`Did you mean ${email.split("@")[0]}@${misspellings[emailDomain]}?`);
      return false;
    }

    setEmailError("");
    return true;
  };

  const sendOTP = async () => {
    if (!validateEmail(emailValue)) {
      toast({
        title: "Invalid email",
        description: emailError,
        variant: "destructive",
      });
      return;
    }
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ type: "email", identifier: emailValue }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send OTP");
      }

      setShowOTP(true);
      toast({
        title: "OTP Sent",
        description: "Please check your email for the verification code.",
      });
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send OTP",
        variant: "destructive",
      });
    }
  };

  const verifyOTP = async () => {
    if (otp.length !== 6) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ type: "email", identifier: emailValue, code: otp }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Invalid verification code");
      }

      form.setValue("emailVerified", true);
      setShowOTP(false);
      toast({
        title: "Email verified",
        description: "Your email has been verified successfully.",
      });
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast({
        title: "Verification failed",
        description: error instanceof Error ? error.message : "Invalid verification code",
        variant: "destructive",
      });
    }
  };

  return (
    <FormField
      control={form.control}
      name="email"
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel className={fieldState.error || emailError ? "text-destructive" : ""}>Email</FormLabel>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Email for booking confirmations and event updates</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <FormControl>
                <Input 
                  placeholder="you@example.com" 
                  type="email" 
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    validateEmail(e.target.value);
                  }}
                  disabled={isVerified}
                  className={`flex-1 ${isVerified ? "bg-muted" : "bg-background/50"}`}
                />
              </FormControl>
              {emailError && !isVerified && (
                <p className="text-sm text-destructive mt-1">{emailError}</p>
              )}
            </div>
            {!isVerified && (
              <Button type="button" variant="outline" onClick={sendOTP} className="whitespace-nowrap w-full sm:w-auto">
                Send Code
              </Button>
            )}
            {isVerified && (
              <div className="flex items-center justify-center px-3 py-2 bg-green-50 dark:bg-green-950 rounded-md">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            )}
          </div>

          {showOTP && !isVerified && (
            <div className="mt-4 p-4 border border-border rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground mb-3">
                Enter the 6-digit code sent to your email
              </p>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup className="w-full">
                    <InputOTPSlot index={0} className="bg-background flex-1" />
                    <InputOTPSlot index={1} className="bg-background flex-1" />
                    <InputOTPSlot index={2} className="bg-background flex-1" />
                    <InputOTPSlot index={3} className="bg-background flex-1" />
                    <InputOTPSlot index={4} className="bg-background flex-1" />
                    <InputOTPSlot index={5} className="bg-background flex-1" />
                  </InputOTPGroup>
                </InputOTP>
                <Button type="button" onClick={verifyOTP} disabled={otp.length !== 6} className="w-full sm:w-auto">
                  Verify
                </Button>
              </div>
            </div>
          )}
        </FormItem>
      )}
    />
  );
};

export default EmailField;
